import React from 'react';
import { 
  Row, Col, Card, Statistic, Progress, Tag, Tooltip, Empty, 
  Typography, Space, Alert
} from 'antd';
import { 
  CheckCircleOutlined, 
  FieldTimeOutlined, 
  CloseCircleOutlined, 
  CarOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  EyeInvisibleOutlined 
} from '@ant-design/icons';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ReferenceLine,
  ResponsiveContainer 
} from 'recharts';
import dayjs from 'dayjs';


const { Text } = Typography;

const TrackedPerformanceOverviewTab = ({ 
  stopData, 
  selectedStopTypes, 
  setSelectedStopTypes, 
  dateRange, 
  tripData,
  filterDataByStopType 
}) => {
  // Filter the data by selected stop types
  const filteredStopData = filterDataByStopType(stopData);

  // Calculate aggregated tracking stats for the filtered data
  const calculateAggregatedStats = () => {
    if (!filteredStopData || filteredStopData.length === 0) {
      return {
        trackedPercent: 0,
        untrackedPercent: 0,
        total: 0,
        trackedCount: 0,
        untrackedCount: 0
      };
    }

    let trackedCount = 0;
    let untrackedCount = 0;
    let totalCount = 0;

    filteredStopData.forEach(record => {
      totalCount++;
      
      // Check if the stop is tracked - either ACTUAL_DEPARTURE_TIME or ACTUAL_ARRIVAL_TIME is available
      const hasActualDeparture = Number.isFinite(record.ACTUAL_DEPARTURE_TIME);
      const hasActualArrival = Number.isFinite(record.ACTUAL_ARRIVAL_TIME);
      
      if (hasActualDeparture || hasActualArrival) {
        trackedCount++;
      } else {
        untrackedCount++;
      }
    });

    return {
      trackedPercent: totalCount > 0 ? (trackedCount / totalCount) * 100 : 0,
      untrackedPercent: totalCount > 0 ? (untrackedCount / totalCount) * 100 : 0,
      total: totalCount,
      trackedCount: trackedCount,
      untrackedCount: untrackedCount
    };
  };

  const aggregatedStats = calculateAggregatedStats();

  // Handle stop type selection change
  const handleStopTypeChange = (types) => {
    // Ensure at least one option is selected
    if (types.length === 0) {
      setSelectedStopTypes(['F']); // Default to "First" if nothing selected
      return;
    }
    
    // Handle "All" option
    if (types.includes('ALL')) {
      // If "All" was just selected, make it the only selection
      if (!selectedStopTypes.includes('ALL')) {
        setSelectedStopTypes(['ALL']);
      } else {
        // If other options were also selected, remove "All"
        const otherTypes = types.filter(type => type !== 'ALL');
        if (otherTypes.length > 0) {
          setSelectedStopTypes(otherTypes);
        } else {
          // Keep "All" if it's the only selection
          setSelectedStopTypes(['ALL']);
        }
      }
    } else {
      setSelectedStopTypes(types);
    }
  };

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Row gutter={16}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title={
                      <Space>
                        <span>Tracking Coverage</span>
                        <Tooltip title="Percentage of stops where actual arrival or departure times are recorded. This indicates how well the fleet is being monitored through GPS or other tracking systems.">
                          <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                      </Space>
                    }
                    value={Math.round(aggregatedStats.trackedPercent)}
                    suffix="%"
                    valueStyle={{ color: '#3f8600' }}
                    prefix={<EyeOutlined />}
                  />
                </Card>
              </Col>
              
              <Col span={6}>
                <Card>
                  <Statistic
                    title={
                      <Space>
                        <span>Untracked Stops</span>
                        <Tooltip title="Percentage of stops where no actual timing data was recorded. These stops may indicate GPS issues, vehicle breakdowns, or gaps in tracking coverage.">
                          <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                      </Space>
                    }
                    value={Math.round(aggregatedStats.untrackedPercent)}
                    suffix="%"
                    valueStyle={{ color: '#fa541c' }}
                    prefix={<EyeInvisibleOutlined />}
                  />
                </Card>
              </Col>
              
              <Col span={6}>
                <Card>
                  <Statistic
                    title={
                      <Space>
                        <span>Total Tracked Stops</span>
                        <Tooltip title="Total number of individual stop observations that have actual timing data recorded during the selected time period.">
                          <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                      </Space>
                    }
                    value={aggregatedStats.trackedCount}
                    valueStyle={{ color: '#1890ff' }}
                    prefix={<FieldTimeOutlined />}
                  />
                </Card>
              </Col>
              
              <Col span={6}>
                <Card>
                  <Statistic
                    title={
                      <Space>
                        <span>Analysed Trips</span>
                        <Tooltip title="Total number of route variants (unique trip patterns) analysed within the selected time period. Each variant represents a unique route and direction combination.">
                          <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                      </Space>
                    }
                    value={tripData.length}
                    prefix={<CarOutlined />}
                  />
                </Card>
              </Col>
            </Row>

            {/* Tracking Trend Section */}
            <Row gutter={16} style={{ marginTop: '16px' }}>
              <Col span={18}>
                <Card 
                  title={
                    <Space>
                      <span>Daily Tracking Coverage</span>
                      <Tooltip title="Shows the daily tracking coverage across the selected date range. Each bar represents the percentage of stops with actual timing data for a single day.">
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    </Space>
                  }
                >
                  {(() => {
                    if (!filteredStopData || filteredStopData.length === 0) return <Empty description="No data available" />;
                    
                    // Group data by date to calculate daily tracking coverage
                    const dailyTracking = {};
                    
                    filteredStopData.forEach(record => {
                      const dateId = record.DATE_ID;
                      if (!dateId) return;
                      
                      const dateStr = String(dateId);
                      if (!dailyTracking[dateStr]) {
                        dailyTracking[dateStr] = {
                          date: dateStr,
                          displayDate: record.DATE_FORMATTED || dayjs(dateStr).format('YYYY-MM-DD'),
                          totalStops: 0,
                          trackedStops: 0,
                          untrackedStops: 0,
                          departureDataCount: 0,
                          arrivalDataCount: 0,
                          noDataCount: 0
                        };
                      }
                      
                      dailyTracking[dateStr].totalStops++;
                      
                      // Check if the stop is tracked - either ACTUAL_DEPARTURE_TIME or ACTUAL_ARRIVAL_TIME is available
                      const hasActualDeparture = Number.isFinite(record.ACTUAL_DEPARTURE_TIME);
                      const hasActualArrival = Number.isFinite(record.ACTUAL_ARRIVAL_TIME);
                      
                      if (hasActualDeparture || hasActualArrival) {
                        dailyTracking[dateStr].trackedStops++;
                        
                        if (hasActualDeparture) {
                          dailyTracking[dateStr].departureDataCount++;
                        }
                        if (hasActualArrival) {
                          dailyTracking[dateStr].arrivalDataCount++;
                        }
                      } else {
                        dailyTracking[dateStr].untrackedStops++;
                        dailyTracking[dateStr].noDataCount++;
                      }
                    });
                    
                    // Convert to array and sort by date
                    const dailyTrackingArray = Object.values(dailyTracking)
                      .map(day => ({
                        ...day,
                        trackingPercent: day.totalStops > 0 ? (day.trackedStops / day.totalStops) * 100 : 0
                      }))
                      .sort((a, b) => a.date.localeCompare(b.date));
                    
                    // Ensure all dates in the selected range are included
                    const allDates = [];
                    if (dateRange && dateRange[0] && dateRange[1]) {
                      let currentDate = dateRange[0].clone();
                      const endDate = dateRange[1].clone();
                      
                      while (currentDate.isSameOrBefore(endDate, 'day')) {
                        const dateStr = currentDate.format('YYYYMMDD');
                        if (!dailyTracking[dateStr]) {
                          allDates.push({
                            date: dateStr,
                            displayDate: currentDate.format('YYYY-MM-DD'),
                            totalStops: 0,
                            trackedStops: 0,
                            untrackedStops: 0,
                            departureDataCount: 0,
                            arrivalDataCount: 0,
                            noDataCount: 0,
                            trackingPercent: 0
                          });
                        }
                        currentDate = currentDate.add(1, 'day');
                      }
                    }
                    
                    // Combine existing data with placeholder data for missing dates
                    const completeTrackingArray = [...dailyTrackingArray, ...allDates]
                      .sort((a, b) => {
                        const dateA = dayjs(a.date);
                        const dateB = dayjs(b.date);
                        return dateA.valueOf() - dateB.valueOf();
                      });
                    
                    // Prepare chart data
                    const chartData = completeTrackingArray.map(day => {
                      let displayDate;
                      try {
                        const dateObj = dayjs(day.date);
                        if (dateObj.isValid()) {
                          displayDate = dateObj.format('DD/MM');
                        } else {
                          displayDate = day.displayDate;
                        }
                      } catch (e) {
                        displayDate = day.displayDate;
                      }
                      
                      return {
                        date: displayDate,
                        trackingValue: Math.round(day.trackingPercent),
                        hasData: day.totalStops > 0,
                        totalObservations: day.totalStops,
                        trackedCount: day.trackedStops,
                        untrackedCount: day.untrackedStops,
                        departureCount: day.departureDataCount,
                        arrivalCount: day.arrivalDataCount,
                        noDataCount: day.noDataCount,
                        originalDate: day.date
                      };
                    });
                    
                    return (
                      <div style={{ height: '350px', width: '100%', marginLeft: '-15px' }}>
                        {chartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%" minWidth={600}>
                            <BarChart
                              data={chartData}
                              margin={{ top: 25, right: 20, left: 0, bottom: 25 }}
                              barSize={45}
                              barGap={0}
                              maxBarSize={120}
                            >
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                              <XAxis 
                                dataKey="date" 
                                angle={0} 
                                textAnchor="middle"
                                height={40}
                                tickMargin={5}
                                axisLine={false}
                                tick={{ fontSize: 12, fill: '#666' }}
                                interval="preserveStartEnd"
                                tickLine={false}
                              />
                              <YAxis 
                                domain={[0, 100]}
                                axisLine={{ stroke: '#e5e5e5' }}
                                tick={{ fontSize: 11, fill: '#666' }}
                                tickLine={false}
                                width={20}
                                padding={{ left: 0 }}
                              />
                              <RechartsTooltip 
                                formatter={(value, name) => {
                                  let status = 'Poor Coverage';
                                  let color = '#f5222d';
                                  
                                  if (value >= 90) {
                                    status = 'Excellent Coverage';
                                    color = '#52c41a';
                                  } else if (value >= 70) {
                                    status = 'Good Coverage';
                                    color = '#faad14';
                                  } else if (value >= 50) {
                                    status = 'Fair Coverage';
                                    color = '#fa8c16';
                                  }
                                  
                                  return [
                                    <span style={{ color }}>{`${value}% (${status})`}</span>, 
                                    'Tracking Coverage'
                                  ];
                                }}
                                labelFormatter={(label, payload) => {
                                  if (payload && payload.length > 0) {
                                    const entry = payload[0].payload;
                                    return (
                                      <span style={{ display: 'block' }}>
                                        <span style={{ display: 'block' }}><strong>Date: {label}</strong></span>
                                        <span style={{ display: 'block', marginTop: '8px', fontSize: '11px' }}>
                                          <span style={{ display: 'block' }}>Total Stops: {entry.totalObservations}</span>
                                          <span style={{ display: 'block' }}>Tracked Stops: {entry.trackedCount} ({Math.round((entry.trackedCount/entry.totalObservations)*100) || 0}%)</span>
                                          <span style={{ display: 'block' }}>Untracked Stops: {entry.untrackedCount} ({Math.round((entry.untrackedCount/entry.totalObservations)*100) || 0}%)</span>
                                          <span style={{ display: 'block', marginTop: '4px', borderTop: '1px solid #f0f0f0', paddingTop: '4px' }}>
                                            <span style={{ display: 'block' }}>Departure Data: {entry.departureCount} records</span>
                                            <span style={{ display: 'block' }}>Arrival Data: {entry.arrivalCount} records</span>
                                          </span>
                                        </span>
                                      </span>
                                    );
                                  }
                                  return `Date: ${label}`;
                                }}
                                contentStyle={{ 
                                  borderRadius: '4px', 
                                  border: 'none', 
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)', 
                                  padding: '8px 12px',
                                  fontSize: '12px',
                                  minWidth: '200px'
                                }}
                              />
                              <ReferenceLine 
                                y={90} 
                                stroke="#52c41a" 
                                strokeDasharray="3 3"
                                label={{ 
                                  value: '90', 
                                  position: 'right',
                                  fill: '#52c41a',
                                  fontSize: 12
                                }}
                              />
                              <ReferenceLine 
                                y={70} 
                                stroke="#faad14" 
                                strokeDasharray="3 3"
                                label={{ 
                                  value: '70', 
                                  position: 'right',
                                  fill: '#faad14',
                                  fontSize: 12
                                }}
                              />
                              <ReferenceLine 
                                y={50} 
                                stroke="#fa8c16" 
                                strokeDasharray="3 3"
                                label={{ 
                                  value: '50', 
                                  position: 'right',
                                  fill: '#fa8c16',
                                  fontSize: 12
                                }}
                              />
                              <Bar 
                                dataKey="trackingValue" 
                                name="Tracking Coverage" 
                                fill="#52c41a"
                                radius={[4, 4, 0, 0]}
                                shape={(props) => {
                                  if (!props.payload.hasData) {
                                    return null;
                                  }
                                  
                                  let fill = '#f5222d';
                                  
                                  if (props.payload.trackingValue >= 90) {
                                    fill = '#52c41a';
                                  } else if (props.payload.trackingValue >= 70) {
                                    fill = '#faad14';
                                  } else if (props.payload.trackingValue >= 50) {
                                    fill = '#fa8c16';
                                  }
                                  
                                  return (
                                    <g>
                                      <rect
                                        x={props.x}
                                        y={props.y}
                                        width={props.width}
                                        height={props.height}
                                        fill={fill}
                                        radius={[4, 4, 0, 0]}
                                        rx={4}
                                      />
                                      <text
                                        x={props.x + props.width / 2}
                                        y={props.y - 10}
                                        textAnchor="middle"
                                        fill={fill}
                                        fontSize={11}
                                        fontWeight="bold"
                                        fontFamily="'Helvetica Neue', Arial, sans-serif"
                                      >
                                        {props.payload.trackingValue}%
                                      </text>
                                    </g>
                                  );
                                }}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <Empty description="No data available" />
                        )}
                      </div>
                    );
                  })()}
                </Card>
              </Col>

              <Col span={6}>
                <Card
                  title={
                    <Space>
                      <span>Tracking Trend</span>
                      <Tooltip title="Shows the trend in tracking coverage between the first and second half of the selected period. A positive value indicates improved data collection.">
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    </Space>
                  }
                  style={{ height: '100%' }}
                >
                  {(() => {
                    if (!filteredStopData || filteredStopData.length === 0) return <Empty description="No data available" />;
                    
                    // Group data by date to calculate daily tracking coverage
                    const dailyTracking = {};
                    
                    filteredStopData.forEach(record => {
                      const dateId = record.DATE_ID;
                      if (!dateId) return;
                      
                      const dateStr = String(dateId);
                      if (!dailyTracking[dateStr]) {
                        dailyTracking[dateStr] = {
                          date: dateStr,
                          displayDate: record.DATE_FORMATTED || dayjs(dateStr).format('YYYY-MM-DD'),
                          totalStops: 0,
                          trackedStops: 0
                        };
                      }
                      
                      dailyTracking[dateStr].totalStops++;
                      
                      // Check if the stop is tracked - either ACTUAL_DEPARTURE_TIME or ACTUAL_ARRIVAL_TIME is available
                      const hasActualDeparture = Number.isFinite(record.ACTUAL_DEPARTURE_TIME);
                      const hasActualArrival = Number.isFinite(record.ACTUAL_ARRIVAL_TIME);
                      
                      if (hasActualDeparture || hasActualArrival) {
                        dailyTracking[dateStr].trackedStops++;
                      }
                    });
                    
                    // Convert to array and sort by date
                    const dailyTrackingArray = Object.values(dailyTracking)
                      .map(day => ({
                        ...day,
                        trackingPercent: day.totalStops > 0 ? (day.trackedStops / day.totalStops) * 100 : 0
                      }))
                      .sort((a, b) => a.date.localeCompare(b.date));
                    
                    // Add trend analysis by comparing first and second half
                    const midpoint = Math.floor(dailyTrackingArray.length / 2);
                    let firstHalfTracking = 0;
                    let secondHalfTracking = 0;
                    
                    if (dailyTrackingArray.length > 0) {
                      const firstHalf = dailyTrackingArray.slice(0, midpoint);
                      const secondHalf = dailyTrackingArray.slice(midpoint);
                      
                      firstHalfTracking = firstHalf.reduce((sum, day) => sum + day.trackingPercent, 0) / firstHalf.length;
                      secondHalfTracking = secondHalf.reduce((sum, day) => sum + day.trackingPercent, 0) / secondHalf.length;
                    }
                    
                    const trendPercentage = firstHalfTracking > 0 
                      ? ((secondHalfTracking - firstHalfTracking) / firstHalfTracking) * 100 
                      : 0;
                    
                    const trendText = trendPercentage >= 0 
                      ? `+${trendPercentage.toFixed(1)}%` 
                      : `${trendPercentage.toFixed(1)}%`;
                    
                    const isPositive = trendPercentage >= 0;
                    
                    // Add labels to indicate which stop types are being shown
                    const stopTypeLabels = {
                      'ALL': 'All Stops',
                      'F': 'First Stops',
                      'M': 'Mid Stops',
                      'L': 'Last Stops'
                    };
                    
                    const selectedTypeLabels = selectedStopTypes.map(type => stopTypeLabels[type] || type).join(', ');
                    
                    return (
                      <div style={{ textAlign: 'center', padding: '30px 0' }}>
                        <Statistic
                          value={trendText}
                          valueStyle={{ 
                            color: isPositive ? '#3f8600' : '#cf1322',
                            fontSize: '36px'
                          }}
                          prefix={isPositive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                          suffix={
                            <div style={{ fontSize: '14px', marginTop: '8px' }}>
                              from previous period
                            </div>
                          }
                        />
                        <div style={{ marginTop: '16px' }}>
                          <Text type="secondary">
                            First half: {firstHalfTracking.toFixed(1)}%
                          </Text>
                          <br />
                          <Text type="secondary">
                            Second half: {secondHalfTracking.toFixed(1)}%
                          </Text>
                          <br />
                          <Tag color="blue" style={{ marginTop: '8px' }}>
                            {selectedTypeLabels}
                          </Tag>
                        </div>
                      </div>
                    );
                  })()}
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TrackedPerformanceOverviewTab; 